import React, { useEffect, useState } from "react";
import { Box, Divider, Typography, Grid } from "@mui/material";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import { WillOpenCardKey } from "src/api/request/requestService";
import { referenceService } from "src/api/references/referenceService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { useTranslate } from "src/locales";

interface InvestigationRequestNoteModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (note: string) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const InvestigationRequestNoteModal: React.FC<
  InvestigationRequestNoteModalProps
> = ({ open, onClose, onSave, additionalInfoKey, additionalInfoParams }) => {
  const { t } = useTranslate();
  const [note, setNote] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));

  // AdditionalInfo/fields üzerinden EXAMINATION_REQUEST form tanımını oku
  // (Şu an için sadece backend ile senkron kalmak için; not alanı serbest text)
  useEffect(() => {
    if (!open) return;

    const loadAdditionalInfo = async () => {
      try {
        const res = await referenceService.additionalInfo.getFields({
          formtype: "EXAMINATION_REQUEST",
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        setAdditionalInfo(res);
        // Not alanı zaten serbest; response sadece form tanımı için tutuluyor.
      } catch {
        // Hata durumunda mevcut manuel akış devam etsin.
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoParams, selectedOfficeId]);

  const handleSave = () => {
    onSave?.(note.trim());
    if (!onSave) {
    onClose();
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.examination.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ maxWidth: 800, mx: "auto", width: "100%" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t("patient-request.examination.note-label")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={7}>
              <ByzMuiTextField
                fullWidth
                size="small"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("patient-request.examination.note-placeholder")}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default InvestigationRequestNoteModal;

